import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React from 'react'
import { RedirectUrls } from 'src/types/types'

export type IDirectoryISection = {
  title: string
  description?: string
  icon?: React.ReactNode
  items: {
    label: string
    url: string
    count?: number
  }[]
}

export type IDirectoryBreadcrumb = {
  title: string
  url: string
}

interface IProps {
  titleDir: string
  subtitle?: string
  sections: IDirectoryISection[]
  breadcrumbs?: IDirectoryBreadcrumb[]
}

function Directory({ titleDir, subtitle, sections, breadcrumbs }: IProps) {
  const { t } = useTranslation('common')

  return (
    <div className="bg-white-500">
      {/* ── Hero strip — cream, editorial chrome ─────────────────────────── */}
      <section className="border-b border-ink-100/15 bg-cream-300">
        <div className="mx-auto max-w-7xl px-5 pt-[calc(var(--nav-h)+40px)] pb-12 md:px-10 md:pt-[calc(var(--nav-h)+64px)] md:pb-16">
          {/* Editorial breadcrumbs — small caps */}
          <nav
            aria-label="breadcrumb"
            className="mb-8 flex flex-wrap items-center gap-2 font-display text-[11px] tracking-[0.18em] text-ink-200 uppercase md:text-xs"
          >
            <Link href="/" className="transition-colors hover:text-blue-500">
              {t('DIRECTORY.HOME')}
            </Link>
            <span aria-hidden="true" className="text-ink-100/40">
              /
            </span>
            <Link
              href={RedirectUrls.Directory}
              className={
                breadcrumbs?.length
                  ? 'transition-colors hover:text-blue-500'
                  : 'font-semibold text-ink-700'
              }
            >
              {t('DIRECTORY.EYEBROW')}
            </Link>
            {breadcrumbs?.map(({ title, url }, index) => {
              const isLast = index === breadcrumbs.length - 1
              return (
                <React.Fragment key={title}>
                  <span aria-hidden="true" className="text-ink-100/40">
                    /
                  </span>
                  <Link
                    href={url}
                    className={
                      isLast
                        ? 'font-semibold text-ink-700'
                        : 'transition-colors hover:text-blue-500'
                    }
                  >
                    {title}
                  </Link>
                </React.Fragment>
              )
            })}
          </nav>

          {/* Eyebrow */}
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            {t('DIRECTORY.EYEBROW')}
          </p>

          {/* Editorial H1 — capitalized titles look right with Fraunces */}
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-[1.05] font-medium tracking-[-0.015em] text-ink-700 capitalize md:text-[56px]">
            {titleDir}
          </h1>

          {subtitle ? (
            <p className="mt-5 max-w-2xl text-[15px] leading-relaxed text-ink-200 md:mt-6 md:text-[17px]">
              {subtitle}
            </p>
          ) : null}
        </div>
      </section>

      {/* ── Sections — 12-col rail with hairline link rows ───────────────── */}
      <div className="mx-auto max-w-7xl px-5 py-16 md:px-10 md:py-24">
        <div className="space-y-16 md:space-y-24">
          {sections.map(({ title, description, items }) => (
            <section
              key={title}
              className="border-t border-ink-100/15 pt-10 md:grid md:grid-cols-12 md:gap-12 md:pt-14"
            >
              {/* Left rail — eyebrow + title + count + description */}
              <div className="md:col-span-4">
                <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                  {t('DIRECTORY.ITEMS_COUNT', { count: items.length })}
                </p>
                <h2 className="mt-3 font-display text-[22px] leading-[1.15] font-medium text-ink-700 md:text-[28px]">
                  {title}
                </h2>
                {description ? (
                  <p className="mt-3 max-w-sm text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                    {description}
                  </p>
                ) : null}
              </div>

              {/* Right rail — link grid, hairline rows */}
              <ul className="mt-8 grid grid-cols-1 border-t border-ink-100/15 md:col-span-8 md:mt-0 md:grid-cols-2">
                {items.map((item, idx) => {
                  const isRightCol = idx % 2 === 1
                  return (
                    <li
                      key={item.url}
                      className={[
                        'border-b border-ink-100/15',
                        // vertical hairline between columns on desktop
                        isRightCol ? 'md:border-l md:border-ink-100/15' : ''
                      ].join(' ')}
                    >
                      <Link
                        href={item.url}
                        prefetch={false}
                        className="group flex items-center justify-between gap-3 px-1 py-3.5 text-[14px] text-ink-700 capitalize transition-colors hover:text-blue-500 md:px-5 md:py-4 md:text-[15px]"
                      >
                        <span className="truncate">{item.label}</span>
                        <NorthEastRoundedIcon
                          className="shrink-0 text-ink-100/60 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-blue-500"
                          fontSize="small"
                        />
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  )
}

export default React.memo(Directory)
